import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

const focusable = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Drawer is a modal panel from the left edge, as a tablet's alerts are. Drawn
// on <body> so everything else can be made inert while it is open; focus goes
// in, Tab stays in, and Escape or the backdrop closes it. Marked modal without
// any of that, focus stayed on the button behind the backdrop (#287).
export function Drawer({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  const panel = useRef<HTMLDivElement>(null)
  const [host] = useState(() => document.createElement('div'))
  useEffect(() => {
    // Focus returns to the opener after inert comes off: focusing an element
    // still inert does nothing, which jsdom does not show (#287).
    const opener = document.activeElement as HTMLElement | null
    document.body.appendChild(host)
    const behind = Array.from(document.body.children).filter((el) => el !== host && !el.hasAttribute('inert'))
    for (const el of behind) el.setAttribute('inert', '')
    panel.current?.querySelector<HTMLElement>(focusable)?.focus()
    return () => {
      for (const el of behind) el.removeAttribute('inert')
      host.remove()
      opener?.focus()
    }
  }, [host])

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') return onClose()
    if (e.key !== 'Tab' || !panel.current) return
    const items = Array.from(panel.current.querySelectorAll<HTMLElement>(focusable))
    if (items.length === 0) return
    const first = items[0]
    const last = items[items.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-40">
      <div data-testid="drawer-backdrop" aria-hidden="true" className="absolute inset-0 bg-black/55" onClick={onClose} />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onKeyDown={onKeyDown}
        className="absolute left-0 top-0 bottom-0 w-[22.5rem] max-w-[88vw] border-r border-zinc-800 shadow-[16px_0_40px_rgb(0_0_0/0.45)]"
      >
        {children}
      </div>
    </div>,
    host,
  )
}
